/**
 * Free-ball physics (owner: ball team): gravity (Hail Mary floats), wind on a live flight, the ellipse's angle
 * (a pass's nose follows its arc, a toss tumbles end over end, a tipped/bounced ball tumbles freely with
 * damping), goalpost collisions (crossbar knob, upright tip, stanchion — DOINK) with substeps so a fast ball never
 * tunnels through a post, and ground contact with an angle-dependent rigid-body bounce (a ball landing on its
 * nose kicks up and spins, a flat one skids). A live flight touching the ground goes DEAD (holder -1) and keeps
 * fgAttempt / forwardPass / thrownBy / releaseX for the rules to classify on the same tick — except a KICK
 * (`ball.kick`), which goes LOOSE and keeps bouncing / rolling as a live ball.
 */
import {
  BALL_HALF_LENGTH,
  DT,
  END_LINE_LEFT,
  END_LINE_RIGHT,
  GOALPOST_CROSSBAR_RADIUS,
  GOALPOST_CROSSBAR_Y,
  GOALPOST_STANCHION_HALF_WIDTH,
  GOALPOST_UPRIGHT_TIP_RADIUS,
  GOALPOST_UPRIGHT_TOP_Y,
  HITSTOP,
  KICK,
  MIDFIELD_X,
  MOTION,
  PHYSICS,
  PI,
  SPECIALS_TUNING,
  datan2Into,
} from '@hf/shared';
import { Once, addHitStop, firstThisStep } from '../contact/fx';
import { BallState } from '../enums';
import { ballBeyondLos } from '../selectors';
import type { SimEventSink } from '../sink';
import type { BallSim, SimState } from '../state';
import { ballImpulse, dir, imp, setRotation, support, supportAlongDir } from './shape';

const POST_CROSSBAR = 0;
const POST_TIP = 1;
const POST_STANCHION = 2;
const POST_PARTS = ['crossbar', 'upright', 'stanchion'] as const;

/** Per-tick gravity (precomputed: no double is merged with a constant on this per-tick path). */
const GRAVITY_STEP = PHYSICS.ballGravity * DT;
const GRAVITY_HAIL_MARY = PHYSICS.ballGravity * DT * SPECIALS_TUNING.hailMary.gravityMul;

/** Margin (yd) around an end line inside which post collisions are tested. */
const POST_ZONE = 1;

/** Deepest post contact of the current substep. */
const hit = { part: -1, nx: 0.5, ny: 0.5, pen: 0.5 };
/** Ground contact result: impact (closing) speed of the last `collideGround`. */
const ground = { impact: 0.5 };
const TAU = 2 * PI;

/**
 * Post circles per part (crossbar knob, upright tip, stanchion): the circle centre's y is clamped to
 * [POST_CY_LO, POST_CY_HI] (a point for the knob and the tip; the closest point of the stanchion segment
 * x = endX, y ∈ [0, CROSSBAR_Y] for the stanchion), radius POST_R. Typed arrays: the per-substep loop below reads
 * plain doubles (no double merged with a constant, no call per circle: allocation-free).
 */
const POST_CY_LO = new Float64Array([GOALPOST_CROSSBAR_Y, GOALPOST_UPRIGHT_TOP_Y, 0]);
const POST_CY_HI = new Float64Array([GOALPOST_CROSSBAR_Y, GOALPOST_UPRIGHT_TOP_Y, GOALPOST_CROSSBAR_Y]);
const POST_R = new Float64Array([
  GOALPOST_CROSSBAR_RADIUS,
  GOALPOST_UPRIGHT_TIP_RADIUS,
  GOALPOST_STANCHION_HALF_WIDTH,
]);

/** Collide the ball with the goalposts of the nearer end. Returns the post part hit (-1 none). */
const collidePosts = (b: BallSim): number => {
  const endX = b.x > MIDFIELD_X ? END_LINE_RIGHT : END_LINE_LEFT;
  const ax = b.x - endX;
  if ((ax < 0 ? -ax : ax) > POST_ZONE) return -1;
  setRotation(b);
  hit.part = -1;
  hit.pen = 0;
  for (let part = POST_CROSSBAR; part <= POST_STANCHION; part++) {
    // the deepest contact among the three circles wins
    const r = POST_R[part]!;
    const dx = b.x - endX;
    const dy = b.y - Math.min(Math.max(b.y, POST_CY_LO[part]!), POST_CY_HI[part]!);
    const d2 = dx * dx + dy * dy;
    const reach = BALL_HALF_LENGTH + r + MOTION.ballSubstepMax; // broad phase
    if (d2 > reach * reach || d2 < 1e-18) continue;
    const d = Math.sqrt(d2);
    dir.x = -dx / d;
    dir.y = -dy / d;
    supportAlongDir();
    const pen = support.h + r - d;
    if (pen > hit.pen) {
      hit.part = part;
      hit.nx = dx / d;
      hit.ny = dy / d;
      hit.pen = pen;
    }
  }
  if (hit.part < 0) return -1;
  b.x += hit.nx * hit.pen;
  b.y += hit.ny * hit.pen;
  dir.x = -hit.nx;
  dir.y = -hit.ny;
  supportAlongDir();
  imp.nx = hit.nx;
  imp.ny = hit.ny;
  imp.rx = support.x;
  imp.ry = support.y;
  imp.e = PHYSICS.ballPostBounce;
  imp.mu = MOTION.ballGroundFrictionCoef;
  ballImpulse(b);
  return hit.part;
};

/** Ground contact: push out, bounce. Returns 1 when touching (impact speed in `ground.impact`), else 0. */
const collideGround = (b: BallSim): number => {
  setRotation(b);
  dir.x = 0;
  dir.y = -1;
  supportAlongDir();
  const pen = support.h - b.y;
  if (pen < 0) return 0;
  b.y += pen;
  const vcy = b.vy + b.angVel * support.x;
  imp.nx = 0;
  imp.ny = 1;
  imp.rx = support.x;
  imp.ry = support.y;
  imp.e = -vcy > MOTION.ballRestSpeed ? PHYSICS.ballBounce : 0;
  imp.mu = MOTION.ballGroundFrictionCoef;
  ballImpulse(b);
  ground.impact = imp.approach;
  return 1;
};

const emitBounce = (s: SimState, ev: SimEventSink): void => {
  const speed = ground.impact;
  if (ev && speed >= MOTION.ballBounceEventSpeed && firstThisStep(Once.BALL_BOUNCE, -1))
    ev.push({ type: 'BALL_BOUNCE', tick: s.tick, actor: -1, x: s.ball.x, y: 0, speed });
};

/** Heading scratch (datan2Into: no double returned from a call on this per-tick path). */
const heading = { v: 0.5 };

/** A clean pass is a spiral: its nose follows the arc. */
const noseFollow = (b: BallSim): void => {
  datan2Into(heading, b.vy, b.vx);
  const d = heading.v - b.angle;
  b.angVel = MOTION.ballNoseFollow * (d - PI * Math.round(d / PI));
};

/** Substeps this tick: 1 in open field, enough near the goalposts that a fast ball never tunnels a post. */
const substepsFor = (b: BallSim): number => {
  const step = Math.sqrt(b.vx * b.vx + b.vy * b.vy) * DT;
  const endX = b.x > MIDFIELD_X ? END_LINE_RIGHT : END_LINE_LEFT;
  const ax = b.x - endX;
  if ((ax < 0 ? -ax : ax) > POST_ZONE + step) return 1;
  const n = Math.ceil(step / MOTION.ballSubstepMax);
  return n < 1 ? 1 : n > MOTION.ballMaxSubsteps ? MOTION.ballMaxSubsteps : n;
};

/** Move by one of `n` substeps and collide with the posts; returns the post part hit (-1 none). */
const substep = (b: BallSim, n: number): number => {
  const dt = DT / n;
  b.x += b.vx * dt;
  b.y += b.vy * dt;
  const a = b.angle + b.angVel * dt;
  b.angle = a > PI || a < -PI ? a - TAU * Math.round(a / TAU) : a;
  return collidePosts(b);
};

/** Integrate one tick of a free ball. `live` = a catchable flight (AIR in RUSH_COUNT / LIVE). */
export const flyBall = (s: SimState, ev: SimEventSink, live: boolean): void => {
  const b = s.ball;
  const airborne = b.state === BallState.AIR;
  b.vy -= airborne && b.hailMary ? GRAVITY_HAIL_MARY : GRAVITY_STEP;
  if (airborne) {
    b.airFrames++;
    b.vx += s.rules.windX * DT;
    const clean = b.touchedMask === 0 && b.bounces === 0;
    if (clean && !b.fgAttempt && !b.kick) noseFollow(b);
    else if (!clean) b.angVel *= 1 - MOTION.ballAirSpinDamping * DT;
  }
  const n = substepsFor(b);
  let postPart = -1;
  for (let k = 0; k < n; k++) {
    const part = substep(b, n);
    if (part >= 0 && postPart < 0) postPart = part;
  }
  if (postPart >= 0 && airborne) {
    b.bounces++;
    if (live) {
      if (ev && firstThisStep(Once.POST_HIT, -1))
        ev.push({ type: 'POST_HIT', tick: s.tick, actor: -1, x: b.x, y: b.y, part: POST_PARTS[postPart]! });
      addHitStop(s, ev, -1, HITSTOP.postHit, 'post', b.x, b.y);
    }
  }
  if (airborne && ballBeyondLos(s)) b.passedLos = true;
  if (collideGround(b) === 0) return;
  emitBounce(s, ev);
  // a kick bleeds speed on every impact (it hops and dies instead of skidding 30 yards)
  if (b.kick && ground.impact > MOTION.ballRestSpeed) b.vx *= KICK.looseBounceKeep;
  if (airborne) {
    if (b.kick) {
      // A kick stays live where it lands: it bounces on as a LOOSE ball (rules + ball/loose.ts).
      b.state = BallState.LOOSE;
      b.looseFrames = 0;
      return;
    }
    // The flight is over: incomplete / FG short (rules classify it this tick).
    b.state = BallState.DEAD;
    b.holder = -1;
    return;
  }
  b.vx *= 1 - (b.kick ? KICK.looseGroundFriction : PHYSICS.ballGroundFriction) * DT;
  b.angVel *= 1 - MOTION.ballGroundSpinDamping * DT;
  const sl = MOTION.ballSleepSpeed;
  if (b.vx < sl && b.vx > -sl && b.vy < sl && b.vy > -sl && b.angVel < sl && b.angVel > -sl) {
    b.vx = 0;
    b.vy = 0;
    b.angVel = 0;
  }
};
